/**
 * Session Manager – Verwaltet persistente Lesesitzungen in IndexedDB.
 * Jede Session speichert den Originaltext, das verarbeitete HTML und Metadaten.
 *
 * Für die Synchronisation zwischen Geräten trägt jede Sitzung ein
 * Änderungsdatum (`updatedAt`), und Löschen hinterlässt eine Markierung
 * (`deletedAt`) statt die Sitzung zu entfernen – sonst käme sie vom anderen
 * Gerät zurück. Markierungen verfallen nach TOMBSTONE_DAYS.
 */

const STORAGE_KEY = 'the-reader-sessions';
const DB_NAME = 'TheReaderDB';
const STORE_NAME = 'sessions';
// Version 2: updatedAt für bestehende Sitzungen nachgetragen
const DB_VERSION = 2;

const DAY_MS = 24 * 60 * 60 * 1000;

/** So lange bleiben Löschmarkierungen stehen (Tage) */
export const TOMBSTONE_DAYS = 90;

const changeListeners = [];

let dbPromise = null;

/**
 * Initializes/opens the IndexedDB database.
 * Also performs automatic migration from localStorage if old data exists.
 * @returns {Promise<IDBDatabase>}
 */
function initDB() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        return;
      }
      if (event.oldVersion < 2) {
        addMissingUpdatedAt(event.target.transaction.objectStore(STORE_NAME));
      }
    };

    request.onblocked = () => {
      console.warn(
        'Sitzungs-Datenbank wartet: Ein anderer Reader-Tab nutzt noch die alte Version.'
      );
    };

    request.onsuccess = async (event) => {
      const db = event.target.result;
      // Künftige Umstellungen nicht blockieren, wenn dieser Tab offen bleibt
      db.onversionchange = () => db.close();

      // Migrate from localStorage if old data exists
      const oldData = localStorage.getItem(STORAGE_KEY);
      if (oldData) {
        try {
          const sessions = JSON.parse(oldData);
          if (Array.isArray(sessions) && sessions.length > 0) {
            console.log(`Migrating ${sessions.length} sessions from localStorage to IndexedDB...`);
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            for (const session of sessions) {
              store.put({ updatedAt: session.createdAt, ...session });
            }

            await new Promise((resolveTx, rejectTx) => {
              transaction.oncomplete = () => resolveTx();
              transaction.onerror = (e) => rejectTx(e.target.error);
            });
            console.log('Migration successful.');
          }
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          // Alte Daten bleiben liegen; beim nächsten Start wird es erneut versucht
          console.error('Error migrating sessions from localStorage:', e);
        }
      }

      resolve(db);
    };

    request.onerror = (event) => {
      console.error('Failed to open IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });

  return dbPromise;
}

/**
 * Trägt bei Sitzungen aus Version 1 das Änderungsdatum nach
 * @param {IDBObjectStore} store
 */
function addMissingUpdatedAt(store) {
  store.openCursor().onsuccess = (event) => {
    const cursor = event.target.result;
    if (!cursor) return;
    if (!cursor.value.updatedAt) {
      cursor.update({ ...cursor.value, updatedAt: cursor.value.createdAt });
    }
    cursor.continue();
  };
}

/**
 * @typedef {Object} Session
 * @property {string} id - Eindeutige Session-ID (UUID)
 * @property {string} title - Automatisch generierter Titel
 * @property {number} createdAt - Erstellungszeitpunkt (Timestamp)
 * @property {number} lastOpenedAt - Letzter Zugriff (Timestamp)
 * @property {number} updatedAt - Letzte Änderung von Text oder Titel (Timestamp)
 * @property {number} [deletedAt] - Gesetzt, wenn die Sitzung gelöscht wurde
 * @property {number} totalChars - Gesamtzeichenanzahl
 * @property {number} segmentCount - Anzahl der Segmente
 * @property {string} rawText - Original-Eingabetext
 * @property {string} processedHtml - Bereinigtes HTML nach Verarbeitung
 */

/**
 * Generiert eine UUID v4
 * @returns {string}
 */
function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback
  return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 9);
}

/**
 * Generiert einen Titel aus den Segmenten.
 * Verwendet die erste Überschrift oder die ersten ~60 Zeichen des Textes.
 * @param {import('./textSegmenter.js').Segment[]} segments
 * @returns {string}
 */
export function generateTitle(segments) {
  if (!segments || segments.length === 0) return 'Unbenannte Sitzung';

  // Try to find the first heading
  const heading = segments.find((s) => s.type === 'heading' || s.type === 'heading-with-text');

  if (heading) {
    // For heading-with-text, extract only the heading part
    const text = heading.plainText.split('. ')[0] || heading.plainText;
    return text.length > 80 ? text.substring(0, 77) + '…' : text;
  }

  // Fallback: first ~60 characters of the first segment
  const first = segments[0];
  if (first) {
    const text = first.plainText;
    return text.length > 60 ? text.substring(0, 57) + '…' : text;
  }

  return 'Unbenannte Sitzung';
}

/**
 * Speichert eine neue Session oder aktualisiert eine bestehende.
 * @param {string} rawText - Original-Eingabetext
 * @param {string} processedHtml - Verarbeitetes HTML
 * @param {import('./textSegmenter.js').Segment[]} segments - Segmente
 * @param {string} [title] - Optionaler benutzerdefinierter Titel (z.B. Dateiname)
 * @returns {Promise<Session>} Die gespeicherte Session
 */
export async function saveSession(rawText, processedHtml, segments, title = null) {
  const now = Date.now();

  const totalChars = segments.reduce((sum, seg) => sum + seg.charCount, 0);

  const session = {
    id: generateId(),
    title: title || generateTitle(segments),
    createdAt: now,
    lastOpenedAt: now,
    updatedAt: now,
    totalChars,
    segmentCount: segments.length,
    rawText,
    processedHtml,
  };

  await putSession(session);
  return session;
}

/**
 * Gibt alle Sessions zurück, sortiert nach lastOpenedAt (neueste zuerst)
 * @returns {Promise<Session[]>}
 */
export async function getAllSessions() {
  const records = await getAllRecords();
  return records
    .filter((record) => !record.deletedAt)
    .sort((a, b) => b.lastOpenedAt - a.lastOpenedAt);
}

/**
 * Gibt alle Datensätze zurück, auch Löschmarkierungen
 * @returns {Promise<Session[]>}
 */
export async function getAllRecords() {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const request = db.transaction([STORE_NAME], 'readonly').objectStore(STORE_NAME).getAll();
    request.onsuccess = (event) => resolve(event.target.result || []);
    request.onerror = (event) => reject(event.target.error);
  });
}

/**
 * Gibt eine bestimmte Session zurück
 * @param {string} id
 * @returns {Promise<Session|null>}
 */
export async function getSession(id) {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = (event) => {
      const record = event.target.result;
      resolve(record && !record.deletedAt ? record : null);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
}

/**
 * Löscht eine Session. Übrig bleibt eine Markierung ohne Inhalt, damit sich
 * das Löschen auf andere Geräte überträgt.
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function deleteSession(id) {
  const now = Date.now();
  await putSession({ id, deletedAt: now, updatedAt: now });
}

/**
 * Entfernt Löschmarkierungen, die älter als TOMBSTONE_DAYS sind
 * @returns {Promise<number>} Anzahl der entfernten Markierungen
 */
export async function purgeTombstones() {
  const cutoff = Date.now() - TOMBSTONE_DAYS * DAY_MS;
  const expired = (await getAllRecords()).filter(
    (record) => record.deletedAt && record.deletedAt < cutoff
  );
  if (expired.length === 0) return 0;

  const db = await initDB();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  for (const record of expired) {
    store.delete(record.id);
  }
  await new Promise((resolve, reject) => {
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  return expired.length;
}

/**
 * Löscht alle Sitzungen, die länger als `days` Tage nicht geöffnet wurden.
 * @param {number} days - Aufbewahrungsdauer in Tagen; 0 löscht nichts
 * @returns {Promise<number>} Anzahl der gelöschten Sitzungen
 */
export async function deleteOlderThan(days) {
  if (!days || days <= 0) return 0;

  const cutoff = Date.now() - days * DAY_MS;
  const sessions = await getAllSessions();
  const expired = sessions.filter((session) => session.lastOpenedAt < cutoff);

  for (const session of expired) {
    await deleteSession(session.id);
  }
  return expired.length;
}

/**
 * Aktualisiert den lastOpenedAt-Timestamp einer Session
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function updateLastOpened(id) {
  await updateLiveSession(id, { lastOpenedAt: Date.now() });
}

/**
 * Berechnet die ungefähre Größe aller Sessions in IndexedDB (in Bytes)
 * @returns {Promise<number>}
 */
export async function getStorageSize() {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    } catch {
      // Fallback below
    }
  }

  // Fallback: estimate size based on serialization
  try {
    const sessions = await getAllSessions();
    const str = JSON.stringify(sessions);
    return new Blob([str]).size;
  } catch {
    return 0;
  }
}

/**
 * Formatiert eine Byte-Größe in eine lesbare Darstellung
 * @param {number} bytes
 * @returns {string}
 */
export function formatStorageSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Formatiert einen Timestamp in ein deutsches Datum mit Uhrzeit
 * @param {number} timestamp
 * @returns {string}
 */
export function formatDate(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Ersetzt den Text einer bestehenden Sitzung. Titel, Zeichen- und
 * Abschnittszahl werden aus dem neuen Text abgeleitet.
 * @param {string} id
 * @param {string} rawText - Neuer Original-Eingabetext
 * @param {string} processedHtml - Neues verarbeitetes HTML
 * @param {import('./textSegmenter.js').Segment[]} segments - Neue Segmente
 * @returns {Promise<boolean>} false, wenn es die Sitzung nicht (mehr) gibt
 */
export async function replaceSessionText(id, rawText, processedHtml, segments) {
  return updateLiveSession(id, {
    title: generateTitle(segments),
    lastOpenedAt: Date.now(),
    updatedAt: Date.now(),
    totalChars: segments.reduce((sum, seg) => sum + seg.charCount, 0),
    segmentCount: segments.length,
    rawText,
    processedHtml,
  });
}

/**
 * Schreibt eine Sitzung in die Datenbank
 * @param {Session} session
 * @returns {Promise<void>}
 */
async function putSession(session) {
  await writeRecords([session]);
  notifyChange();
}

/**
 * Übernimmt abgeglichene Datensätze (auch Löschmarkierungen) in einer
 * Transaktion – außer dort, wo hier inzwischen eine neuere Fassung steht
 * (geändert, während der Abgleich lief). Meldet keine Änderung, sonst stieße
 * das Übernehmen den nächsten Abgleich an.
 * @param {Session[]} records
 * @returns {Promise<void>}
 */
export async function applyRecords(records) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    for (const record of records) {
      store.get(record.id).onsuccess = (event) => {
        const existing = event.target.result;
        if (!existing || existing.updatedAt <= record.updatedAt) store.put(record);
      };
    }
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * @param {Session[]} records
 * @returns {Promise<void>}
 */
async function writeRecords(records) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    for (const record of records) store.put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Ändert eine bestehende, nicht gelöschte Sitzung. Lesen und Schreiben laufen
 * in einer Transaktion – sonst könnte eine Änderung eine gerade in einem
 * anderen Tab gesetzte Löschmarkierung überschreiben.
 * @param {string} id
 * @param {Partial<Session>} changes
 * @returns {Promise<boolean>} false, wenn es die Sitzung nicht (mehr) gibt
 */
async function updateLiveSession(id, changes) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    let updated = false;

    store.get(id).onsuccess = (event) => {
      const session = event.target.result;
      if (!session || session.deletedAt) return;
      store.put({ ...session, ...changes });
      updated = true;
    };
    transaction.oncomplete = () => {
      if (updated) notifyChange();
      resolve(updated);
    };
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Registriert einen Listener für Änderungen an Sitzungen
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onChange(callback) {
  changeListeners.push(callback);
  return () => {
    const idx = changeListeners.indexOf(callback);
    if (idx >= 0) changeListeners.splice(idx, 1);
  };
}

function notifyChange() {
  for (const listener of changeListeners) listener();
}

/**
 * Aktualisiert den Titel einer Sitzung
 * @param {string} id
 * @param {string} newTitle
 * @returns {Promise<void>}
 */
export async function updateSessionTitle(id, newTitle) {
  await updateLiveSession(id, { title: newTitle, updatedAt: Date.now() });
}
